import {
  patternValidator,
  Message,
  minLengthError,
  minLengthValidator,
  maxLengthValidator,
  validateAllErrors,
  TextField,
  maxLengthError
} from 'legos/text-field/base-field';
import { createElement, FC, ReactNode, useMemo } from 'react';
import { NewPasswordErrorMessage } from './new-password-error-message';

export interface NewPasswordFieldProps {
  /** The name of this form field, unique in the current form context */
  name: string;
  /** Label node to display to the user for this field */
  label: ReactNode;
  /** Should display the password in plain text */
  showPlainText?: boolean;
  /** Message to display password constraints */
  passwordConstraintMessage: ReactNode;
  /** Error to show when the field does not have at least one number */
  errorOneNumberMessage: ReactNode;
  /** Error to show when the field does not have at least one UPPERCASE letter */
  errorOneUppercaseLetterMessage: ReactNode;
  /** Error to show when the field does not have at least one lower letter */
  errorOneLowercaseLetterMessage: ReactNode;
  /** Error to show when the field does not meet the min-length requirement */
  errorMinLengthMessage: ReactNode;
  /** Error to show when the field does not meet the max-length requirement */
  errorMaxLengthMessage?: ReactNode;
  /** A hash of error messages to show the user that occurred after a failed form submission */
  submitErrorMessages?: { [submitErrorName: string]: ReactNode };
}

const pwdConstraintOneNumber = 'oneNumber';
const pwdConstraintOneUppercaseLetter = 'oneUpperCaseLetter';
const pwdConstraintOneLowercaseLetter = 'oneLowerCaseLetter';

export const NewPasswordField: FC<NewPasswordFieldProps> = ({
  name,
  label,
  showPlainText,
  passwordConstraintMessage,
  errorOneNumberMessage,
  errorOneUppercaseLetterMessage,
  errorOneLowercaseLetterMessage,
  errorMinLengthMessage,
  errorMaxLengthMessage,
  submitErrorMessages
}) => {
  const validate = useMemo(
    () =>
      validateAllErrors(
        // Must be >= 8
        minLengthValidator(8),
        // Must be <= 60
        maxLengthValidator(60),
        // Must have 1 number
        patternValidator(/[0-9]/, pwdConstraintOneNumber),
        // Must have 1 uppercase letter
        patternValidator(/(?=.*[A-Z])/, pwdConstraintOneUppercaseLetter),
        // Must have 1 lowercase letter
        patternValidator(/(?=.*[a-z])/, pwdConstraintOneLowercaseLetter)
      ),
    []
  );

  return (
    <TextField
      autoComplete="new-password"
      type={showPlainText ? 'text' : 'password'}
      validate={validate}
      name={name}
      label={label}
      multiMessages
      required
    >
      <Message>
        <NewPasswordErrorMessage children={passwordConstraintMessage} />
      </Message>
      <Message>
        {(props: object) => (
          <NewPasswordErrorMessage
            values={props}
            constraint={pwdConstraintOneNumber}
            children={errorOneNumberMessage}
          />
        )}
      </Message>
      <Message>
        {(props: object) => (
          <NewPasswordErrorMessage
            values={props}
            constraint={pwdConstraintOneUppercaseLetter}
            children={errorOneUppercaseLetterMessage}
          />
        )}
      </Message>
      <Message>
        {(props: object) => (
          <NewPasswordErrorMessage
            values={props}
            constraint={pwdConstraintOneLowercaseLetter}
            children={errorOneLowercaseLetterMessage}
          />
        )}
      </Message>
      <Message>
        {(props: object) => (
          <NewPasswordErrorMessage
            values={props}
            constraint={minLengthError}
            children={errorMinLengthMessage}
          />
        )}
      </Message>
      <Message>
        {(props: object) => (
          <NewPasswordErrorMessage
            values={props}
            constraint={maxLengthError}
            children={errorMaxLengthMessage}
          />
        )}
      </Message>
      {submitErrorMessages &&
        Object.entries(submitErrorMessages).map(([error, node]) => (
          <Message
            key={error}
            submitError={error}
            dirtySinceLastSubmit={false}
            children={node}
          />
        ))}
    </TextField>
  );
};
